import { useCallback, useEffect, useRef } from 'react'
import {
  ExecuteAIAgentRequest,
  InteractWithAIAgentRequest,
  useExecuteAiAgentStreamingMutation,
  useInteractWithAiAgentStreamingMutation,
} from '../../../store/ai/aiAgent'
import { type UseStreamingChatProps, useStreamingChat } from './useStreamingChat'

type AbortableInteraction = Promise<unknown> & { abort: () => void }

export const useStreamingAiAgent = <T>(props: UseStreamingChatProps<T>) => {
  const { onStreamingEvent } = useStreamingChat<T>(props)
  const [triggerInteract] = useInteractWithAiAgentStreamingMutation()
  const [triggerExecute] = useExecuteAiAgentStreamingMutation()

  const inFlightInteractions = useRef(new Set<AbortableInteraction>())

  useEffect(() => {
    const inFlight = inFlightInteractions.current
    return () => {
      inFlight.forEach((interaction) => {
        interaction.abort()
      })
    }
  }, [])

  const track = useCallback(<P extends AbortableInteraction>(interaction: P): P => {
    inFlightInteractions.current.add(interaction)
    interaction.finally(() => inFlightInteractions.current.delete(interaction))
    return interaction
  }, [])

  const triggerInteractWithAiAgentStreaming = useCallback(
    (request: InteractWithAIAgentRequest) => track(triggerInteract({ ...request, onStreamingEvent })),
    [onStreamingEvent, track, triggerInteract],
  )

  const triggerExecuteAiAgentStreaming = useCallback(
    (request: ExecuteAIAgentRequest) => track(triggerExecute({ ...request, onStreamingEvent })),
    [onStreamingEvent, track, triggerExecute],
  )

  return { triggerInteractWithAiAgentStreaming, triggerExecuteAiAgentStreaming }
}
